import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/sharp-regular-svg-icons';
import { faClose } from '@fortawesome/sharp-solid-svg-icons';
import { useRouter } from 'next/navigation';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Typography from '@/components/typography';
import { LAYERS_URL } from '@/constants/urls';
import { useLayerLibraryContext } from '@/features/layerLibrary/provider';
import { LayerLibraryOverlayCard } from '@/features/navigation/bottom-navigation/toolkit/layerLibraryOverlay/card';
import { ReactSetState } from '@/models/reactTypes';

type Props = {
  open: boolean;
  setOpen: ReactSetState<Boolean>;
};

export const LayerLibraryOverlay: React.FC<Props> = ({ open, setOpen }) => {
  const router = useRouter();
  const { enabledArdaLayers, clearArdaLayers, setLegendLayerId } =
    useLayerLibraryContext();

  const isEmpty = !enabledArdaLayers.length;

  return (
    <div
      className={twMerge(
        'z-[100] w-[240px] rounded-md bg-white shadow-md',
        !open && 'hidden'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b-1 border-gray-20 p-4">
        <div className="flex items-center gap-x-2">
          <FontAwesomeIcon icon={faLayerGroup} />
          <Typography variant="h5">Layers</Typography>
        </div>
        <IconButton
          size={ButtonSize.SMALL}
          variant={ButtonVariant.GHOST}
          onClick={() => {
            setLegendLayerId(null);
            setOpen(false);
          }}
        >
          <FontAwesomeIcon className="text-gray-70" icon={faClose} />
        </IconButton>
      </div>

      {/* Body */}
      <div className="flex max-h-[650px] flex-col gap-y-3 overflow-auto rounded-b-md bg-gray-10 p-3 scrollbar-hide">
        {isEmpty && (
          <>
            <Typography variant="body2">
              Layers added to your map will appear here.
            </Typography>
            <Button
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              onClick={() => router.push(LAYERS_URL)}
            >
              Open Layers
            </Button>
          </>
        )}
        {!isEmpty && (
          <>
            {enabledArdaLayers.map((layer) => (
              <LayerLibraryOverlayCard key={layer.id} layer={layer} />
            ))}
          </>
        )}
      </div>

      {/* Footer */}
      {!isEmpty && (
        <div className="sticky bottom-0 flex border-t-1 border-gray-20 p-3">
          <Button
            className="w-full"
            size={ButtonSize.SMALL}
            variant={ButtonVariant.SECONDARY}
            onClick={clearArdaLayers}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};
